import type { CampaignSummaryRow } from '@monitoring-panel/types';
import { formatCount, formatInt, formatPercent } from '@/lib/report-format';
import { formatDate } from '@/lib/utils';

interface ReportKpiHeaderProps {
  summary: CampaignSummaryRow;
}

interface Kpi {
  label: string;
  value: string;
  hint?: string;
}

/**
 * KPI header built from the single `campaign_summary` row.
 */
export function ReportKpiHeader({ summary }: ReportKpiHeaderProps) {
  const kpis: Kpi[] = [
    { label: 'Impressions', value: formatCount(summary.impressions_count) },
    { label: 'Clicks', value: formatCount(summary.clicks_count) },
    { label: 'CTR', value: formatPercent(summary.ctr) },
    { label: 'Viewability', value: formatPercent(summary.viewability_rate) },
    { label: 'Measurable rate', value: formatPercent(summary.measurable_rate) },
  ];

  const structure: Kpi[] = [
    { label: 'Insertion orders', value: formatInt(summary.insertion_orders_count) },
    { label: 'Line items', value: formatInt(summary.line_items_count) },
    { label: 'Domains', value: formatInt(summary.domains_count) },
    { label: 'URLs', value: formatInt(summary.urls_count) },
    { label: 'Days active', value: formatInt(summary.days_active) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-adv-body-m text-adv-secondary">
        <span className="adv-num font-bold text-adv-primary">
          {formatDate(summary.start_date)} — {formatDate(summary.end_date)}
        </span>
        <span>
          <span className="adv-num">{summary.days_active}</span> days active
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {structure.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} subtle />
        ))}
      </div>
    </div>
  );
}

function KpiCard({ kpi, subtle }: { kpi: Kpi; subtle?: boolean }) {
  return (
    <div
      className={
        subtle
          ? 'bg-adv-sunken rounded-adv-field px-4 py-3'
          : 'adv-card adv-card--kpi px-4 py-4'
      }
    >
      <p className="adv-kpi__label truncate">{kpi.label}</p>
      <p
        className={
          subtle
            ? 'mt-1 adv-num text-adv-code-m font-bold text-adv-primary'
            : 'mt-2 adv-kpi__value text-adv-primary'
        }
      >
        {kpi.value}
      </p>
      {kpi.hint && <p className="text-adv-body-s text-adv-tertiary mt-1">{kpi.hint}</p>}
    </div>
  );
}
